import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import type { ConversationDetail, Uuid } from '@zenda/api-contract';
import { ClientTag } from './ClientTag';
import { Icon } from './Icon';
import { Marcar } from './Marcar';
import { useSemPiloto } from '../lib/piloto';
import { PatientDrawer } from './PatientDrawer';
import { Skeleton } from './Skeleton';
import { useTip } from './Tip';
import { useBooking, useClients, usePatientAgenda } from '../lib/queries';
import { rotaDaConversa } from '../lib/rotas';
import {
  formatCountdown,
  formatDateShort,
  formatPhone,
  formatRelative,
  initials,
} from '../lib/format';
import { useT } from '../lib/i18n';

/**
 * A ficha do contato — a terceira coluna do inbox, recolhida por padrão.
 *
 * ── Por que ela é um componente, e não parte da página ──
 * `InboxPage` tinha 1203 linhas e três estados que só existiam aqui: qual consulta
 * está sendo desmarcada, se a confirmação de "desmarcar todas" está aberta, e se o
 * painel de marcação está aberto. Nenhum deles diz nada à lista ou à conversa, e
 * misturados no topo da página pareciam estado da página.
 *
 * O que atravessa a fronteira é uma coisa só: o texto que a marcação produz precisa
 * cair no campo de resposta, que vive na coluna do meio. Por isso `onTextoPronto` — a
 * ficha não sabe que existe um campo, e a página não sabe que existe uma marcação.
 */
export function FichaDoContato({
  conversation,
  conversationId,
  onTextoPronto,
}: {
  /** `undefined` enquanto o detalhe carrega: a ficha mostra o esqueleto. */
  readonly conversation: ConversationDetail['conversation'] | undefined;
  readonly conversationId: string | null;
  /** O texto pronto da marcação, para a página levar ao campo de resposta. */
  readonly onTextoPronto: (texto: string) => void;
}): ReactElement {
  const t = useT();
  const f = t.inbox.fichaAcoes;
  const tip = useTip();
  const [marcando, setMarcando] = useState(false);
  const [desmarcando, setDesmarcando] = useState<string | null>(null);
  const [desmarcarTodas, setDesmarcarTodas] = useState(false);
  const [fichaAberta, setFichaAberta] = useState(false);
  const [marcarCom, setMarcarCom] = useState<Uuid | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const clientes = useClients();
  const agendaDoPaciente = usePatientAgenda(conversation?.contactId ?? null);
  const { desmarcar, desmarcarTudo } = useBooking(
    conversation?.contactId ?? null,
    conversationId,
    conversation?.clientId,
  );

  if (conversation === undefined) {
    return (
      <aside className="card inbox__side" id="ficha-contato" aria-label={t.inbox.contactCard}>
        <div style={{ display: 'grid', gap: 14 }}>
          <Skeleton h={64} radius="var(--radius-md)" />
          <Skeleton h={16} w="60%" />
          <Skeleton h={16} w="80%" />
          <Skeleton h={90} radius="var(--radius-md)" />
        </div>
      </aside>
    );
  }

  const todas = agendaDoPaciente.data?.upcoming ?? [];
  const proximas = todas.filter((a) => a.clientId === conversation.clientId);
  const deOutros = todas.filter((a) => a.clientId !== conversation.clientId);
  const outrasConversas = (agendaDoPaciente.data?.conversations ?? []).filter(
    (c) => c.clientId !== conversation.clientId,
  );
  const deDependentes = proximas.filter((a) => a.dependentId !== null).length;
  const ativos = (clientes.data ?? []).filter((c) => c.status !== 'churned');
  const clienteDaMarcacao = marcarCom ?? conversation.clientId;
  const comOutro = clienteDaMarcacao !== conversation.clientId;
  const outro = ativos.find((c) => c.id === clienteDaMarcacao);

  return (
    <aside className="card inbox__side" id="ficha-contato" aria-label={t.inbox.contactCard}>
      {/* Cabeçalho numa linha só: avatar, nome e telefone lado a lado. Empilhados,
          gastavam 96 px de altura para dizer o que a lista ao lado já diz — e
          empurravam a agenda, que é o que ela abre para ver, para fora da tela. */}
      <div className="side__hero">
        <span className="avatar">{initials(conversation.contactName)}</span>
        <div className="side__id">
          <p className="side__name">{conversation.contactName}</p>
          {/* O `wa_id` como reserva: quem chega pelo webhook da Meta vem com ele
              preenchido e `phone_e164` vazio — a Meta manda o WhatsApp ID, e nao um
              telefone declarado. Sem a reserva, todo paciente que nunca foi cadastrado
              a mao mostrava um travessao. */}
          <p className="side__phone num">{formatPhone(conversation.phone ?? conversation.waId)}</p>
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setFichaAberta(true)}
          {...tip(t.inbox.abrirFichaDica)}
        >
          {t.inbox.abrirFicha}
        </button>
      </div>
      {fichaAberta ? (
        <PatientDrawer
          clientId={conversation.clientId}
          contactId={conversation.contactId}
          onClose={() => setFichaAberta(false)}
        />
      ) : null}

      <dl className="side__facts">
        <div>
          <dt>{t.inbox.client}</dt>
          <dd>
            <ClientTag name={conversation.clientName} color={conversation.clientColor} />
          </dd>
        </div>
        <div>
          <dt>{t.inbox.lastMessage}</dt>
          <dd>
            {conversation.lastMessageAt === null ? '—' : formatRelative(conversation.lastMessageAt)}
          </dd>
        </div>
        <div>
          <dt>{t.inbox.windowTitle}</dt>
          <dd>
            {conversation.serviceWindow.open
              ? t.inbox.windowOpenFor(
                  formatCountdown(conversation.serviceWindow.minutesRemaining ?? 0),
                )
              : t.inbox.windowShut}
          </dd>
        </div>
        <SemPiloto conversation={conversation} />
      </dl>

      {outrasConversas.length === 0 ? null : (
        <div className="side__outros">
          <p className="label">{t.inbox.tambemFalaCom}</p>
          <ul>
            {outrasConversas.map((c) => (
              <li key={c.conversationId}>
                <Link to={rotaDaConversa(c.conversationId)} className="side__outro">
                  <ClientTag name={c.clientName} color={c.clientColor} />
                  <Icon name="chevron-right" size={13} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="side__agenda">
        <div className="side__agenda-topo">
          <p className="label">{t.inbox.nextAppointment}</p>
          {marcando ? null : (
            <button type="button" className="side__marcar" onClick={() => setMarcando(true)}>
              <Icon name="plus" size={13} />
              {f.marcar}
            </button>
          )}
        </div>

        {aviso === null ? null : <p className="side__aviso">{aviso}</p>}

        {marcando ? (
          <>
            {ativos.length < 2 ? null : (
              <label className="side__com">
                <span>{t.inbox.marcarCom}</span>
                <select
                  className="input input--sm"
                  value={clienteDaMarcacao}
                  onChange={(e) => setMarcarCom(e.target.value as Uuid)}
                >
                  {ativos.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.id === conversation.clientId ? ` (${t.inbox.desta})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Marcar
              key={clienteDaMarcacao}
              clientId={clienteDaMarcacao}
              contactId={conversation.contactId}
              conversationId={comOutro ? null : conversation.id}
              contactName={conversation.contactName}
              doctorName={comOutro ? (outro?.name ?? '') : conversation.doctorName}
              onMarcado={(texto) => {
                if (comOutro) setAviso(t.inbox.marcadaComOutro(outro?.name ?? ''));
                else onTextoPronto(texto);
              }}
              onFechar={() => {
                setMarcando(false);
                setMarcarCom(null);
              }}
            />
          </>
        ) : proximas.length === 0 ? (
          <p className="side__none">{t.inbox.noAppointment}</p>
        ) : (
          <>
            <ul className="side__consultas">
              {proximas.map((a) => (
                <li
                  key={a.id}
                  className={`side__consulta${desmarcando === a.id ? ' is-confirmando' : ''}`}
                >
                  {/* A confirmação acontece NA LINHA, e não num diálogo. Desmarcar é
                      reversível remarcando, e um modal por cima da conversa tira da tela
                      justamente o contexto que ela está usando para decidir. */}
                  {desmarcando === a.id ? (
                    <>
                      <span className="side__consulta-pergunta">{f.desmarcarHora(a.hora)}</span>
                      <button
                        type="button"
                        className="side__consulta-sim"
                        onClick={() =>
                          desmarcar.mutate({ id: a.id }, { onSuccess: () => setDesmarcando(null) })
                        }
                      >
                        {f.desmarcar}
                      </button>
                      <button
                        type="button"
                        className="side__consulta-nao"
                        onClick={() => setDesmarcando(null)}
                      >
                        {f.nao}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="side__consulta-dia num">{formatDateShort(a.date)}</span>
                      <span className="side__consulta-hora num">{a.hora}</span>
                      <span className="side__consulta-onde">
                        {/* O assunto na frente do local: e o que ela procura ao correr os
                            olhos pela lista. */}
                        {a.dependentName === null ? '' : `${a.dependentName} · `}
                        {a.title === null ? '' : `${a.title} · `}
                        {a.locationName ?? t.inbox.telehealth}
                        {a.seriesId === null ? '' : ` · ${f.serie}`}
                        {a.overbooked ? ` · ${f.encaixe}` : ''}
                      </span>
                      <button
                        type="button"
                        className="side__consulta-x"
                        onClick={() => setDesmarcando(a.id)}
                        {...tip(f.desmarcarSo)}
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {proximas.length < 2 ? null : desmarcarTodas ? (
              <p className="side__tudo-confirma">
                {f.desmarcarN(proximas.length)}
                {deDependentes === 0 ? '' : ` ${t.inbox.incluiDependentes(deDependentes)}`}
                <button
                  type="button"
                  className="side__consulta-sim"
                  onClick={() =>
                    desmarcarTudo.mutate(undefined, {
                      onSuccess: () => setDesmarcarTodas(false),
                    })
                  }
                >
                  {f.desmarcarTodas}
                </button>
                <button
                  type="button"
                  className="side__consulta-nao"
                  onClick={() => setDesmarcarTodas(false)}
                >
                  {f.nao}
                </button>
              </p>
            ) : (
              <button type="button" className="side__tudo" onClick={() => setDesmarcarTodas(true)}>
                {f.desmarcarTodas}
              </button>
            )}
          </>
        )}

        {deOutros.length === 0 || marcando ? null : (
          <div className="side__de-outros">
            <p className="label">{t.inbox.comOutrosMedicos}</p>
            <ul className="side__consultas">
              {deOutros.map((a) => (
                <li key={a.id} className="side__consulta is-de-outro">
                  <span className="side__consulta-dia num">{formatDateShort(a.date)}</span>
                  <span className="side__consulta-hora num">{a.hora}</span>
                  <span className="side__consulta-onde">
                    <ClientTag name={a.clientName} color={a.clientColor} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * A marca "sem piloto" é do PACIENTE, e vale para todos os médicos da operação (plano
 * §6.1). Posta sozinha depois de um teste de IA; a atendente tira aqui.
 */
function SemPiloto({
  conversation,
}: {
  readonly conversation: ConversationDetail['conversation'];
}): ReactElement {
  const t = useT();
  const marcar = useSemPiloto(conversation.contactId);
  const sem = marcar.data?.noAutopilot ?? conversation.autopilot.noAutopilot;
  return (
    <div>
      <dt>{t.piloto.ficha.titulo}</dt>
      <dd>
        {sem
          ? conversation.autopilot.noAutopilotSince === null
            ? t.piloto.ficha.sem
            : t.piloto.ficha.semTexto(
                formatDateShort(conversation.autopilot.noAutopilotSince),
                conversation.autopilot.noAutopilotReason,
              )
          : t.piloto.ficha.com}{' '}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={marcar.isPending}
          onClick={() => marcar.mutate(!sem)}
        >
          {sem ? t.piloto.ficha.tirar : t.piloto.ficha.marcar}
        </button>
      </dd>
    </div>
  );
}
